import type { Image, Project } from '@/.generated/sanityTypes/graphql'
import { walsheim } from '@/lib/fonts'
import { getTypedLocale } from '../lib/typeHelper'
import { cx } from '@monorepo/helper/cx'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/router'
import React, { useEffect } from 'react'
import ReactDOM from 'react-dom'

import Carousel from './carousel'
import { CloseIcon } from './icons'
import Img from './Img'

type ProjectModalProps = {
  imageIndex: number
  isOpen: boolean
  project: Project
  onClose: () => void
  setSelectedImageIndex: (newSelectedImageIndex: number) => void
}

const ProjectModal = ({
  setSelectedImageIndex,
  imageIndex,
  isOpen,
  onClose,
  project,
}: ProjectModalProps) => {
  const locale = getTypedLocale(useRouter().locale)
  const t = useTranslations('projects')
  const squareImageSize = 100

  useEffect(() => {
    // Run this only at runtime
    if (typeof document !== 'undefined') {
      if (isOpen) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = 'auto'
      }
      return () => {
        document.body.style.overflow = 'auto'
      }
    }
  })

  if (project.projectImages && project.projectImages?.length === 0) return null
  const modalPortal =
    typeof document !== `undefined`
      ? ReactDOM.createPortal(
          <div
            className={cx(
              'bg-black-75 fixed inset-0 z-50 flex h-screen w-screen flex-col overflow-hidden',
              isOpen ? 'block' : 'hidden',
              walsheim.className,
            )}
            onClick={onClose}
          >
            <button className="group absolute right-0 top-0 flex cursor-pointer pr-6 pt-4 focus:outline-none">
              <CloseIcon className="text-2xl text-white group-hover:opacity-75" />
              <div className="inline pl-2 text-white group-hover:opacity-75">
                {t('close')}
              </div>
            </button>
            <div className="flex h-full cursor-pointer flex-col items-center pt-16">
              <div
                className="relative mx-auto w-full flex-1 overflow-hidden"
                onClick={(e) => e.stopPropagation()}
              >
                <Carousel
                  onChange={setSelectedImageIndex}
                  className="project-carousel h-full w-full cursor-default pb-20"
                  showArrows={false}
                  showIndicators={false}
                  objectFit="contain"
                  selectedItem={imageIndex}
                  containerStyle={{ height: '100%' }}
                  images={project.projectImages as any}
                  fullscreen
                />
                <div className="absolute bottom-0 w-full">
                  <p
                    className="mx-auto mb-2 w-1/2 overflow-auto text-center text-sm text-white"
                    style={{ minHeight: '1.5rem' }}
                  >
                    {project.projectImages?.[imageIndex]?.description &&
                      project.projectImages?.[imageIndex]?.description?.[
                        locale
                      ]}
                  </p>
                </div>
              </div>
              <div
                className="mx-auto mt-4 flex w-full max-w-screen-lg flex-initial justify-center overflow-auto pb-4 sm:w-3/4"
                onClick={(e) => e.stopPropagation()}
              >
                {project.projectImages?.map((image, index: number) => {
                  const active = imageIndex === index
                  if (!image) return null
                  return (
                    <div
                      key={image?._key}
                      onClick={() => setSelectedImageIndex(index)}
                      className="mr-4 cursor-pointer"
                    >
                      <Img
                        sizes={`${squareImageSize * 2}px`}
                        image={image as Image}
                        className={`h-16 w-16 ${
                          active ? 'opacity-100' : 'opacity-50'
                        } sm:w-25 sm:h-25 hover:opacity-100`}
                      />
                    </div>
                  )
                })}
              </div>
            </div>
          </div>,
          document.body,
        )
      : null

  return <>{isOpen ? modalPortal : null}</>
}

export default ProjectModal
